import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import IntegrationCapabilities from "@/components/IntegrationCapabilities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Phone, KeyRound, Loader2, Save, CheckCircle2, Trash2, PhoneOutgoing,
  Server, BookOpen, XCircle, AlertTriangle,
} from "lucide-react";

function CredsCard({ state, setState, onSave, onTest, onClear, saving, testing }) {
  const onChange = (k, v) => setState((s) => ({ ...s, [k]: v }));
  return (
    <Card className="rounded-sm border-zinc-200">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2 pb-3 border-b border-zinc-100">
          <KeyRound className="h-4 w-4 text-zinc-700" />
          <h2 className="font-display font-bold text-lg tracking-tight">PBX connection</h2>
          {state.configured && (
            <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border-emerald-200 ml-2">
              <CheckCircle2 className="h-3 w-3 mr-1" /> configured
            </Badge>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5 md:col-span-2">
            <Label className="label-overline">Server URL</Label>
            <Input className="rounded-sm font-mono text-[12px]"
              placeholder="https://yourpbx.3cx.com.au:5001"
              value={state.server_url || ""}
              onChange={(e) => onChange("server_url", e.target.value)}
              data-testid="threecx-server-url" />
            <p className="text-[10px] text-zinc-500">Include scheme + port. Trailing slash optional.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="label-overline">API Client ID</Label>
            <Input className="rounded-sm font-mono text-[12px]"
              placeholder="Integrations API Client ID"
              value={state.admin_username || ""}
              onChange={(e) => onChange("admin_username", e.target.value)}
              data-testid="threecx-admin-username" />
            <p className="text-[10px] text-zinc-500">
              From 3CX Admin → Integrations → API → your client.
              <strong> Must have &quot;3CX Call Control API Access&quot; ticked.</strong>
              On V18 (legacy) use the admin extension username instead.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label className="label-overline">API Client Secret</Label>
            <Input className="rounded-sm font-mono text-[12px]" type="password"
              placeholder={state.has_admin_password ? "••••••••••• (saved)" : "Integrations API Client Secret"}
              value={state.admin_password || ""}
              onChange={(e) => onChange("admin_password", e.target.value)}
              data-testid="threecx-admin-password" />
            {state.has_admin_password && !state.admin_password && (
              <p className="text-[10px] text-zinc-500">Leave blank to keep the saved secret.</p>
            )}
            {!state.has_admin_password && (
              <p className="text-[10px] text-zinc-500">
                We authenticate via OAuth2 <code>client_credentials</code>
                {" "}against <code>/connect/token</code> — the same token
                works for both Call Control + Configuration APIs.
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="label-overline">Default extension</Label>
            <Input className="rounded-sm font-mono text-[12px]" placeholder="201"
              value={state.default_extension || ""}
              onChange={(e) => onChange("default_extension", e.target.value)}
              data-testid="threecx-default-extension" />
            <p className="text-[10px] text-zinc-500">Source extension used when an admin clicks a phone number to dial.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="label-overline">CRM template name (informational)</Label>
            <Input className="rounded-sm font-mono text-[12px]" placeholder="Iceberg CRM"
              value={state.crm_template_name || ""}
              onChange={(e) => onChange("crm_template_name", e.target.value)}
              data-testid="threecx-crm-template" />
          </div>
        </div>

        {state.last_verified_at && (
          <div className="text-[11px] text-zinc-500 font-mono">
            Last verified: {state.last_verified_at} · PBX: <strong>{state.fqdn || "—"}</strong>{" "}
            v{state.version || "—"}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-100">
          {state.configured && (
            <Button type="button" variant="ghost" size="sm" className="rounded-sm text-rose-600 hover:bg-rose-50"
              onClick={onClear} data-testid="threecx-clear">
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Clear creds
            </Button>
          )}
          <Button type="button" variant="outline" size="sm" className="rounded-sm"
            onClick={onTest} disabled={testing || !state.configured}
            data-testid="threecx-test">
            {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
            Test connection
          </Button>
          <Button type="button" size="sm" className="rounded-sm"
            onClick={onSave} disabled={saving} data-testid="threecx-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
            Save credentials
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function TestResultCard({ result }) {
  if (!result) return null;
  const cc = result.call_control || {};
  const cfg = result.config_api || {};
  const row = (label, ok, http, detail, extra) => (
    <div className="flex items-start gap-3 py-2" data-testid={`threecx-test-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      {ok ? (
        <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
      ) : (
        <XCircle className="h-4 w-4 text-rose-600 mt-0.5 shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm">{label}</span>
          {http != null && (
            <span className="text-[10px] font-mono text-zinc-500">HTTP {http}</span>
          )}
        </div>
        {extra && <div className="text-[11px] text-zinc-600 mt-0.5">{extra}</div>}
        {detail && !ok && (
          <div className="text-[11px] text-rose-800 bg-rose-50 border border-rose-200 rounded-sm px-2 py-1 mt-1">
            {detail}
          </div>
        )}
      </div>
    </div>
  );
  return (
    <Card className={`rounded-sm ${result.ok ? "border-emerald-200 bg-emerald-50/30" : "border-rose-200 bg-rose-50/30"}`}
      data-testid="threecx-test-result">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 pb-3 border-b border-zinc-100">
          {result.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                     : <AlertTriangle className="h-4 w-4 text-rose-600" />}
          <h2 className="font-display font-bold text-lg tracking-tight">
            {result.ok ? "Connection verified" : "Connection failed"}
          </h2>
        </div>
        {result.error ? (
          <p className="text-sm text-rose-800 mt-3">{result.error}</p>
        ) : (
          <div className="divide-y divide-zinc-100 mt-1">
            {row(
              "Call Control API",
              cc.ok,
              cc.http,
              cc.detail,
              cc.ok
                ? `${cc.active_participants ?? 0} active participant(s) right now`
                : null,
            )}
            {row(
              "Configuration API",
              cfg.ok,
              cfg.http,
              cfg.detail,
              cfg.ok
                ? `${cfg.fqdn || "PBX"}${cfg.version ? ` · v${cfg.version}` : ""}${cfg.extensions_registered != null ? ` · ${cfg.extensions_registered} extension(s) registered` : ""}`
                : null,
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DiagnoseCard() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/threecx/master/diagnose");
      setResult(data);
    } catch (e) {
      setResult({ error: e.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };

  return (
    <Card className="rounded-sm border-zinc-200" data-testid="threecx-diagnose-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 pb-3 border-b border-zinc-100 mb-3">
          <AlertTriangle className="h-4 w-4 text-zinc-700" />
          <h2 className="font-display font-bold text-lg tracking-tight">
            Diagnose 403 / permission errors
          </h2>
          <Button onClick={run} disabled={loading}
            variant="outline" className="rounded-sm ml-auto"
            data-testid="threecx-diagnose-run">
            {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                     : <AlertTriangle className="h-3.5 w-3.5 mr-1.5" />}
            Run diagnostics
          </Button>
        </div>
        <p className="text-[11px] text-zinc-500 mb-3">
          Probes every endpoint the Call Centre relies on and
          cross-references your token&apos;s visible departments against
          the departments containing extensions. Tells you which
          department is missing from your API client&apos;s Role/Department
          grant table.
        </p>

        {result?.error && (
          <div className="rounded-sm border border-rose-200 bg-rose-50 p-3 text-[12px] text-rose-800">
            {result.error}
          </div>
        )}

        {result?.summary && (
          <div className="space-y-1 mb-4">
            {result.summary.map((line, i) => (
              <div key={i} className="text-[12px] font-mono"
                data-testid={`threecx-diagnose-line-${i}`}>{line}</div>
            ))}
          </div>
        )}

        {result?.endpoints && (
          <div className="grid grid-cols-2 gap-2 mb-4">
            {Object.entries(result.endpoints).map(([k, v]) => (
              <div key={k}
                className={`text-[11px] font-mono px-2 py-1 rounded-sm border ${
                  v.ok
                    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                    : "border-rose-200 bg-rose-50 text-rose-800"
                }`}>
                <span className="font-semibold">{k}</span> · HTTP {v.http ?? "—"}
                {v.detail && <div className="text-[10px] mt-0.5 opacity-80">{v.detail.slice(0, 140)}</div>}
              </div>
            ))}
          </div>
        )}

        {result?.departments_visible && result.departments_visible.length > 0 && (
          <div className="mb-3">
            <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-zinc-600">
              Departments visible to token
            </div>
            <div className="text-[12px] mt-1">
              {result.departments_visible.map((d) => d.name).join(" · ") || "—"}
            </div>
          </div>
        )}

        {result?.extensions_by_department && Object.keys(result.extensions_by_department).length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-zinc-600">
              Departments containing extensions
            </div>
            <div className="text-[12px] mt-1 space-y-1">
              {Object.entries(result.extensions_by_department).map(([d, exts]) => (
                <div key={d}>
                  <span className="font-mono font-semibold">{d}</span> —{" "}
                  <span className="text-zinc-600">ext {exts.filter(Boolean).join(", ")}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ClickToCallCard({ state }) {
  const [ext, setExt] = useState("");
  const [dest, setDest] = useState("");
  const [dialing, setDialing] = useState(false);

  const dial = async () => {
    if (!dest.trim()) { toast.error("Enter a destination number."); return; }
    setDialing(true);
    try {
      const { data } = await api.post("/threecx/master/click-to-call", {
        extension: ext.trim() || undefined,
        destination: dest.trim(),
      });
      toast.success(`Dialled from ext ${data.extension} → ${data.destination}`);
      setDest("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dial failed");
    } finally { setDialing(false); }
  };

  return (
    <Card className="rounded-sm border-zinc-200">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2 pb-3 border-b border-zinc-100">
          <PhoneOutgoing className="h-4 w-4 text-zinc-700" />
          <h2 className="font-display font-bold text-lg tracking-tight">Click-to-call test</h2>
        </div>
        {!state.configured && (
          <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
            Save + verify credentials above before dialling.
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label className="label-overline">From extension</Label>
            <Input className="rounded-sm font-mono text-[12px]"
              placeholder={state.default_extension || "201"}
              value={ext} onChange={(e) => setExt(e.target.value)}
              disabled={!state.configured}
              data-testid="threecx-c2c-extension" />
            <p className="text-[10px] text-zinc-500">Blank = default extension.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="label-overline">Destination</Label>
            <Input className="rounded-sm font-mono text-[12px]"
              placeholder="+61400000000 or 305"
              value={dest} onChange={(e) => setDest(e.target.value)}
              disabled={!state.configured}
              data-testid="threecx-c2c-destination" />
          </div>
          <div className="space-y-1.5 md:self-end">
            <Button type="button" size="sm" className="rounded-sm w-full"
              onClick={dial} disabled={!state.configured || dialing}
              data-testid="threecx-c2c-dial">
              {dialing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <PhoneOutgoing className="h-3.5 w-3.5 mr-1" />}
              Place call
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function InfoCard() {
  return (
    <Card className="rounded-sm border-zinc-200 bg-zinc-50">
      <CardContent className="p-6 space-y-2">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-zinc-700" />
          <h2 className="font-display font-bold text-sm tracking-tight">Setup checklist inside 3CX</h2>
        </div>
        <ol className="text-[12px] text-zinc-700 list-decimal ml-5 space-y-1">
          <li>3CX Admin Console → <strong>Integrations → API</strong> → <em>Add</em>.</li>
          <li>Tick <strong>3CX Call Control API Access</strong> (required for click-to-dial + live calls).</li>
          <li>Also grant the <em>System Owner</em> role (or the granular {" "}
            <em>Read Users / Read Calls / Read Voicemails</em>) so
            recordings + voicemails appear on the Call Centre page.</li>
          <li>Copy the generated <strong>Client ID</strong> + <strong>Client Secret</strong> into the fields above and hit <em>Test connection</em>.</li>
        </ol>
        <p className="text-[11px] text-zinc-500 mt-2">
          Requires 3CX V20 SP4+ or 3CX Hosted. Older builds without Call
          Control fall back to the legacy XAPI <code>MakeCall</code>
          endpoint automatically.
        </p>
      </CardContent>
    </Card>
  );
}

export default function ThreeCxMasterPage() {
  const [state, setState] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/threecx/master");
      setState(data || {});
    } catch (e) {
      toast.error("Failed to load 3CX config");
    }
  }, []);

  useEffect(() => {
    (async () => { setLoading(true); await load(); setLoading(false); })();
  }, [load]);

  const onSave = async () => {
    setSaving(true);
    const body = {
      server_url: state.server_url || "",
      admin_username: state.admin_username || "",
      default_extension: state.default_extension || "",
      crm_template_name: state.crm_template_name || "",
    };
    if (state.admin_password) body.admin_password = state.admin_password;
    try {
      const { data } = await api.put("/threecx/master", body);
      setState({ ...data, admin_password: "" });
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const [testResult, setTestResult] = useState(null);

  const onTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/threecx/master/test");
      setTestResult({ ok: true, ...data });
      const fqdn = data.config_api?.fqdn;
      const ver = data.config_api?.version;
      const parts = ["Call Control API ok"];
      if (fqdn) parts.push(`${fqdn}${ver ? ` v${ver}` : ""}`);
      toast.success(`Connected · ${parts.join(" · ")}`);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Detail can be either the structured object we return on failure
      // (call_control + config_api sub-results) or a plain string.
      if (detail && typeof detail === "object" && detail.detail) {
        setTestResult({ ok: false, ...detail.detail });
        toast.error(detail.message || "Test failed");
      } else {
        setTestResult({ ok: false, error: detail || "Test failed" });
        toast.error(detail || "Test failed");
      }
    } finally { setTesting(false); }
  };

  const onClear = async () => {
    if (!window.confirm("Clear 3CX credentials? Click-to-call will stop working until you re-enter them.")) return;
    try {
      await api.delete("/threecx/master");
      toast.success("Credentials cleared");
      setState({});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clear failed");
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-zinc-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading 3CX…
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-5xl" data-testid="threecx-page">
      <div className="pb-4 border-b border-zinc-200">
        <div className="label-overline">Admin · Integrations · 3CX Phone System</div>
        <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">
          3CX Phone API
        </h1>
        <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
          Connect to your 3CX PBX via the <strong>Call Control API</strong>
          {" "}(3CX V20 SP4+ / 3CX Hosted). Powers click-to-dial from client
          contact pages, live call monitoring on the Call Centre page, and —
          via the same OAuth token — voicemail + recording playback through
          the companion Configuration API. Reference:{" "}
          <a className="underline decoration-zinc-400 underline-offset-2"
            href="https://www.3cx.com/docs/call-control-api/" target="_blank" rel="noreferrer">
            Call Control API docs
          </a>
          {" "}·{" "}
          <a className="underline decoration-zinc-400 underline-offset-2"
            href="https://www.3cx.com/docs/configuration-rest-api/" target="_blank" rel="noreferrer">
            Configuration API
          </a>.
        </p>
      </div>

      <CredsCard state={state} setState={setState} onSave={onSave} onTest={onTest}
        onClear={onClear} saving={saving} testing={testing} />

      {testResult && <TestResultCard result={testResult} />}

      <DiagnoseCard />

      <ClickToCallCard state={state} />

      {state.configured && <ExtensionAssignmentsSummary />}

      <InfoCard />

      <IntegrationCapabilities integrationKey="3cx" accent="#0066CC" />
    </div>
  );
}

/* Read-only roll-up of extension → client assignments. Full edit UI
 * lives on /integrations/3cx/call-centre. */
function ExtensionAssignmentsSummary() {
  const [counts, setCounts] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const [asg, clients] = await Promise.all([
          api.get("/threecx/master/extension-assignments"),
          api.get("/clients").catch(() => ({ data: [] })),
        ]);
        const byClient = {};
        for (const [, a] of Object.entries(asg.data?.assignments || {})) {
          if (!a.client_id) continue;
          byClient[a.client_id] = (byClient[a.client_id] || 0) + 1;
        }
        const named = (clients.data || []).map((c) => ({
          client_id: c.client_id,
          company_name: c.company_name || c.client_id,
          count: byClient[c.client_id] || 0,
        })).filter((r) => r.count > 0)
          .sort((a, b) => b.count - a.count);
        setCounts({
          total_assigned: Object.values(byClient).reduce((s, n) => s + n, 0),
          rows: named,
        });
      } catch (e) {
        setError(e.response?.data?.detail || "Failed to load summary");
      }
    })();
  }, []);
  return (
    <Card className="rounded-sm border-zinc-200">
      <CardContent className="p-6 space-y-3">
        <div className="flex items-center gap-2 pb-2 border-b border-zinc-100">
          <Phone className="h-4 w-4 text-zinc-700" />
          <h2 className="font-display font-bold text-lg tracking-tight">
            Client extension assignments
          </h2>
          <span className="ml-auto text-[10px] font-mono text-zinc-500">
            read-only · edit on{" "}
            <a className="underline" href="/integrations/3cx/call-centre">
              Call Centre
            </a>
          </span>
        </div>
        {error ? (
          <div className="text-[12px] text-red-800 bg-red-50 border border-red-200 rounded-sm px-3 py-2">
            {error}
          </div>
        ) : !counts ? (
          <div className="text-[12px] text-zinc-500 flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </div>
        ) : counts.total_assigned === 0 ? (
          <div className="text-[12px] italic text-zinc-500 py-2">
            No extensions have been assigned to clients yet.
          </div>
        ) : (
          <div className="space-y-1.5">
            <div className="text-[12px] text-zinc-600">
              <span className="font-semibold">{counts.total_assigned}</span>{" "}
              extension{counts.total_assigned === 1 ? "" : "s"} mapped across{" "}
              <span className="font-semibold">{counts.rows.length}</span>{" "}
              client{counts.rows.length === 1 ? "" : "s"}.
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1.5 pt-1">
              {counts.rows.map((r) => (
                <div key={r.client_id}
                  className="flex items-center justify-between px-2.5 py-1.5 rounded-sm border border-zinc-200 bg-zinc-50/60">
                  <span className="text-[12px] truncate">{r.company_name}</span>
                  <span className="text-[10px] font-mono text-zinc-600">
                    {r.count} ext{r.count === 1 ? "" : "s"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
